import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import Button from "../../components/Button/Button";
import { Tag } from "../../components/Tag/Tag";

import { useToast } from "../../context/ToastContext";
import { getColorBaseTokenValue, hexToRgba } from "../../design-tokens";

import ProductionApi, { StateParetoResponse, StateParetoRow } from "../../services/ProductionApi";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import { useShiftList } from "../../hooks/useWorkSchedule";
import ShiftFilter from "../../components/ShiftFilter/ShiftFilter";
import PeriodFilter from "../../components/PeriodFilter/PeriodFilter";
import { useActivityTracking } from "../../hooks/useActivityTracking";
import { ActivityEventType } from "../../utilities/activityLog";
import {
  describeSelection,
  DEFAULT_PERIOD_SELECTION,
  PeriodSelection,
  selectionDates,
  selectionReachesToday,
  selectionYmd,
} from "../../utilities/reportRange";
import ParetoChart from "./ParetoChart";
import styles from "./ReportStatePareto.module.css";

type Mode = "time" | "occ";

// Ordem/rótulo/ícone fixos dos 4 estados (as chaves são as do backend).
const STATE_ORDER = ["Produzindo", "CicloExcedido", "Ausencia", "FaltaMaterial"] as const;
const STATE_LABEL_KEY: Record<string, string> = {
  Produzindo: "stProduzindo",
  CicloExcedido: "stExcedido",
  Ausencia: "stAusencia",
  FaltaMaterial: "stMaterial",
};

const ReportStatePareto: React.FC = () => {
  const { t, i18n } = useTranslation("statePareto");
  const { displayToast } = useToast();
  // Log de atividade: emitido SÓ nos handlers abaixo. Instrumentar dentro de load()
  // geraria evento falso a cada push do SignalR (useLiveRefresh).
  const track = useActivityTracking();
  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";

  const [data, setData] = useState<StateParetoResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");
  const [activePeriod, setActivePeriod] = useState<PeriodSelection>(DEFAULT_PERIOD_SELECTION);
  // `null` = "Personalizado" escolhido sem as duas datas ainda → "Aplicar" desabilitado.
  const [pendingPeriod, setPendingPeriod] = useState<PeriodSelection | null>(
    DEFAULT_PERIOD_SELECTION,
  );
  const [mode, setMode] = useState<Mode>("time");

  const shifts = useShiftList();
  const [shiftId, setShiftId] = useState<string | null>(null);

  const fmt = useCallback(
    (v: number, dec = 0): string =>
      Number(v).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec }),
    [locale],
  );
  const fmtDateObj = useCallback(
    (d: Date): string => d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }),
    [locale],
  );

  // `null` no preset de 30 dias: sem `from`/`to` o backend aplica a janela
  // rolante dele, que é a fonte de verdade do padrão da tela.
  const resolveRange = useCallback(
    (sel: PeriodSelection): { from: string; to: string } | null =>
      sel.kind === "preset" && sel.preset === "30" ? null : selectionYmd(sel),
    [],
  );

  const load = useCallback(
    async (sel: PeriodSelection, sid: string | null): Promise<void> => {
      setLoading(true);
      try {
        const range = resolveRange(sel);
        const params = { ...(range ?? {}), ...(sid ? { shiftId: sid } : {}) };
        const resp = await ProductionApi.GetStatePareto(Object.keys(params).length ? params : undefined);
        setData((resp?.data as StateParetoResponse) ?? null);
        setActivePeriod(sel);
        setLastUpdated(new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }));
      } catch (err: any) {
        displayToast("error", t("loadErrorTitle"), err?.errors?.[0] ?? t("loadErrorMsg"), false, 6000);
      } finally {
        setLoading(false);
      }
    },
    [resolveRange, displayToast, t, locale],
  );

  useEffect(() => {
    load(DEFAULT_PERIOD_SELECTION, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stateColor = useMemo(
    (): Record<string, string> => ({
      Produzindo: getColorBaseTokenValue("color.feedback.success.500"),
      CicloExcedido: getColorBaseTokenValue("color.feedback.warning.700"), // Amarelo 700 — distinto de "Ausência"
      Ausencia: "#FF0000", // exibido como "Parada"
      FaltaMaterial: getColorBaseTokenValue("color.feedback.warning.500"), // Amarelo 500 — exibido como "Ausência"
    }),
    [],
  );

  const rangeLabel = useMemo(() => {
    if (data?.from && data?.to) {
      const parse = (s: string): Date => {
        const [y, m, d] = s.split("-").map(Number);
        return new Date(y, m - 1, d);
      };
      return `${fmtDateObj(parse(data.from))} – ${fmtDateObj(parse(data.to))}`;
    }
    // Sem eco do backend, rotula pela janela pedida (inclui o intervalo customizado).
    const { from, to } = selectionDates(activePeriod);
    return `${fmtDateObj(from)} – ${fmtDateObj(to)}`;
  }, [data, activePeriod, fmtDateObj]);

  const derived = useMemo(() => {
    const states: StateParetoRow[] = data?.states ?? [];
    const byKey: Record<string, StateParetoRow> = {};
    states.forEach((s) => {
      byKey[s.state] = s;
    });
    const dim = (s: StateParetoRow): number => (mode === "time" ? s.totalSeconds : s.occurrences);
    const hasData = !!data?.hasData && states.some((s) => s.totalSeconds > 0 || s.occurrences > 0);

    const sorted = [...states].sort((a, b) => dim(b) - dim(a));
    const total = sorted.reduce((acc, s) => acc + dim(s), 0);

    const barValues = sorted.map((s) => (mode === "time" ? s.totalSeconds / 3600 : s.occurrences));
    const share = sorted.map((s) => (total > 0 ? (dim(s) / total) * 100 : 0));
    let acc = 0;
    const cumulative = share.map((sh) => {
      acc += sh;
      return Math.min(100, acc);
    });
    const barColors = sorted.map((s) => stateColor[s.state] ?? "#9ca3af");

    // Cards (sempre por TEMPO): produtivo vs perda, ausência, falta material.
    const totalTime = states.reduce((a, s) => a + s.totalSeconds, 0);
    const secOf = (k: string): number => byKey[k]?.totalSeconds ?? 0;
    const prodSec = secOf("Produzindo");
    const prodPct = totalTime > 0 ? (prodSec / totalTime) * 100 : 0;
    const ausSec = secOf("Ausencia");
    const matSec = secOf("FaltaMaterial");

    return {
      hasData,
      sortedKeys: sorted.map((s) => s.state),
      barValues,
      share,
      cumulative,
      barColors,
      totalTime,
      prodSec,
      prodPct,
      lossPct: 100 - prodPct,
      ausSec,
      ausPct: totalTime > 0 ? (ausSec / totalTime) * 100 : 0,
      matSec,
      matPct: totalTime > 0 ? (matSec / totalTime) * 100 : 0,
    };
  }, [data, mode, stateColor]);

  const stateLabel = (key: string): string => t(STATE_LABEL_KEY[key] ?? key);
  const fmtHours = (sec: number): string => `${fmt(sec / 3600, 1)} ${t("unitH")}`;

  // Atualização em tempo real via SignalR (substitui o botão "Atualizar").
  // Uma janela puramente histórica não muda — não vale reconsultar a cada evento
  // da estação (mesma decisão da Linha do Tempo no modo histórico).
  useLiveRefresh(
    () => void load(activePeriod, shiftId),
    selectionReachesToday(activePeriod),
  );

  const handleApply = (): void => {
    if (!pendingPeriod) return; // intervalo customizado incompleto
    track(ActivityEventType.FilterApply, describeSelection(pendingPeriod));
    void load(pendingPeriod, shiftId);
  };
  const handleReset = (): void => {
    track(ActivityEventType.FilterReset);
    setPendingPeriod(DEFAULT_PERIOD_SELECTION);
    setShiftId(null);
    void load(DEFAULT_PERIOD_SELECTION, null);
  };
  // O turno recarrega na hora (sem "Aplicar"), então carrega com a janela ATIVA.
  const handleShiftChange = (id: string | null): void => {
    track(ActivityEventType.ShiftChange, { shiftId: id });
    setShiftId(id);
    void load(activePeriod, id);
  };

  const chartColors = useMemo(
    () => ({
      bars: derived.barColors,
      text: getColorBaseTokenValue("color.neutral.500"),
      grid: getColorBaseTokenValue("color.neutral.100"),
    }),
    [derived.barColors],
  );

  const iconTint = (hex: string): React.CSSProperties => ({ background: hexToRgba(hex, 0.14), color: hex });

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <Tag
          label={lastUpdated ? `${t("updatedAt")} ${lastUpdated}` : t("loadingLabel")}
          severity="successInverse"
          icon="sync"
        />
      </PageHeader>

      {/* Filtros */}
      <div className={`${styles.card} ${styles.filters}`}>
        <PeriodFilter
          value={pendingPeriod}
          onChange={setPendingPeriod}
          rangeLabel={rangeLabel}
          label={t("fPeriod")}
          presetLabels={{ "7": t("preset7"), "14": t("preset14"), "30": t("preset30") }}
        />
        <ShiftFilter value={shiftId} onChange={handleShiftChange} shifts={shifts} />
        <div className={styles.filterSpacer} />
        <div className={styles.applyWrap}>
          <Button
            label={t("apply")}
            icon="check"
            severity="secondary"
            onClick={handleApply}
            loading={loading}
            disabled={!pendingPeriod}
          />
        </div>
      </div>

      {loading && !data ? (
        <div className={`${styles.card} ${styles.loading}`}>
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
        </div>
      ) : derived.hasData ? (
        <>
          {/* Pareto */}
          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>
                <span className="material-symbols-outlined">bar_chart</span>
                {t("chartTitle")}
              </span>
              <div className={styles.modeGroup}>
                <span className={styles.fieldLabel}>{t("modeLabel")}</span>
                <div className={styles.segmented} role="group" aria-label={t("modeLabel")}>
                  <button
                    type="button"
                    onClick={() => setMode("time")}
                    className={`${styles.segBtn} ${styles.segSmall} ${mode === "time" ? styles.segBtnActive : ""}`}
                  >
                    <span className="material-symbols-outlined">schedule</span>
                    {t("modeTime")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("occ")}
                    className={`${styles.segBtn} ${styles.segSmall} ${mode === "occ" ? styles.segBtnActive : ""}`}
                  >
                    <span className="material-symbols-outlined">tag</span>
                    {t("modeOcc")}
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.chartBody}>
              <ParetoChart
                labels={derived.sortedKeys.map((k) => stateLabel(k))}
                barValues={derived.barValues}
                share={derived.share}
                colors={chartColors}
                valueAxisTitle={mode === "time" ? t("axisHours") : t("axisOcc")}
                unit={mode === "time" ? t("unitH") : t("unitOcc")}
                valueDecimals={mode === "time" ? 1 : 0}
                locale={locale}
              />
            </div>

            <div className={styles.legend}>
              {STATE_ORDER.map((k) => (
                <span key={k} className={styles.legendItem}>
                  <span className={styles.legendSwatch} style={{ background: stateColor[k] }} />
                  {stateLabel(k)}
                </span>
              ))}
            </div>
          </section>

          {/* Resumo de perdas (por tempo) */}
          <div className={styles.summaryGrid}>
            {/* Produtivo vs perda */}
            <div className={styles.summaryCard}>
              <div className={styles.sumHead}>
                <span className={styles.sumLabel}>{t("cProd")}</span>
                <span className={styles.sumIconWrap} style={iconTint(stateColor.Produzindo)}>
                  <span className="material-symbols-outlined">check_circle</span>
                </span>
              </div>
              <div className={styles.sumValueRow}>
                <span className={`${styles.sumValue} ${styles.sumValueGood}`}>{fmt(derived.prodPct, 0)}%</span>
                <span className={styles.sumSub}>{fmtHours(derived.prodSec)}</span>
              </div>
              <div className={styles.splitBar}>
                <span className={styles.splitProd} style={{ width: `${Math.max(0, Math.min(100, derived.prodPct))}%` }} />
                <span className={styles.splitLoss} />
              </div>
              <div className={styles.sumFootRow}>
                <span className={styles.sumFootItem}>
                  <span className={styles.dot} style={{ background: getColorBaseTokenValue("color.feedback.success.500") }} />
                  {t("legProd")}
                </span>
                <span className={styles.sumFootItem}>
                  {t("legLoss")} <span className={styles.lossStrong}>{fmt(derived.lossPct, 0)}%</span>
                </span>
              </div>
            </div>

            {/* Perda na estação (ausência) */}
            <div className={styles.summaryCard}>
              <div className={styles.sumHead}>
                <span className={styles.sumLabel}>{t("cStation")}</span>
                <span className={styles.sumIconWrap} style={iconTint(stateColor.Ausencia)}>
                  <span className="material-symbols-outlined">person_off</span>
                </span>
              </div>
              <div className={styles.sumValueRow}>
                <span className={styles.sumValue}>{fmtHours(derived.ausSec)}</span>
                <span className={styles.sumSub}>{fmt(derived.ausPct, 0)}% {t("ofTotal")}</span>
              </div>
              <div className={styles.ownerRow}>
                <span className="material-symbols-outlined" style={{ color: stateColor.Ausencia }}>badge</span>
                {t("ownerStation")}
              </div>
            </div>

            {/* Perda por abastecimento (falta material) */}
            <div className={styles.summaryCard}>
              <div className={styles.sumHead}>
                <span className={styles.sumLabel}>{t("cSupply")}</span>
                <span className={styles.sumIconWrap} style={iconTint(stateColor.FaltaMaterial)}>
                  <span className="material-symbols-outlined">inventory_2</span>
                </span>
              </div>
              <div className={styles.sumValueRow}>
                <span className={styles.sumValue}>{fmtHours(derived.matSec)}</span>
                <span className={styles.sumSub}>{fmt(derived.matPct, 0)}% {t("ofTotal")}</span>
              </div>
              <div className={styles.ownerRow}>
                <span className="material-symbols-outlined" style={{ color: stateColor.FaltaMaterial }}>local_shipping</span>
                {t("ownerSupply")}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.card}>
          <div className={styles.empty}>
            <span className={`material-symbols-outlined ${styles.emptyIcon}`}>bar_chart_off</span>
            <div>
              <div className={styles.emptyTitle}>{t("emptyTitle")}</div>
              <div className={styles.emptySub}>{t("emptySub")}</div>
            </div>
            <Button label={t("clearFilters")} icon="restart_alt" severity="neutral" variant="outlined" onClick={handleReset} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportStatePareto;
